import React, { Fragment } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import A from 'wanderu-component-react-router-link';
import { Layout } from 'wanderu-components';
import { localize } from 'wanderu-i18n';

import arrow from 'wanderu-assets/images/left-black-arrow.svg';

import {
    CHECKOUT,
    DEPART_DIRECTION,
    RETURN_DIRECTION,
    TRIP_SUMMARY,
} from '../../constants';
import BannerCardList from './BannerCardList/BannerCardList';
import events from './events/SearchBannerEvents';

import styles from './SearchBanner.scss';

const propTypes = {
    bannerText: PropTypes.string,
    changeDepartTripUrl: PropTypes.string,
    changeReturnTripUrl: PropTypes.string,
    className: PropTypes.string,
    'data-id': PropTypes.string,
    departDate: PropTypes.string.isRequired,
    destinationCityName: PropTypes.string,
    i18nMoment: PropTypes.func.isRequired,
    locale: PropTypes.string.isRequired,
    originCityName: PropTypes.string,
    outboundTripId: PropTypes.string,
    returnDate: PropTypes.string,
    returnTripId: PropTypes.string,
    step: PropTypes.string,
    t: PropTypes.func.isRequired,
};

const getSearchBannerState = (
    step,
    text,
    date,
    t,
    hasReturn,
    changeDepartTripUrl,
    outboundTripId,
    changeReturnTripUrl,
    returnTripId,
) => {
    const steps = hasReturn
        ? [DEPART_DIRECTION, RETURN_DIRECTION, TRIP_SUMMARY, CHECKOUT]
        : [DEPART_DIRECTION, TRIP_SUMMARY, CHECKOUT];

    const currentIndex = steps.indexOf(step);
    const preTripSummary = currentIndex < steps.indexOf(TRIP_SUMMARY);

    const departStep = {
        text: t('departure'),
        tripId: outboundTripId,
        tripSummary: !preTripSummary,
        url: changeDepartTripUrl,
    };

    const returnStep = {
        text: t('return'),
    };

    const completeReturnStep = {
        ...returnStep,
        tripId: returnTripId,
        tripSummary: true,
        url: changeReturnTripUrl,
    };

    const tripSummaryStep = {
        text: t('trip-summary'),
    };

    const checkoutStep = {
        text: t('checkout'),
    };

    const toDoStepMap = {
        [CHECKOUT]: checkoutStep,
        [RETURN_DIRECTION]: returnStep,
        [TRIP_SUMMARY]: tripSummaryStep,
    };

    const completeStepMap = {
        [DEPART_DIRECTION]: departStep,
        [RETURN_DIRECTION]: completeReturnStep,
        [TRIP_SUMMARY]: tripSummaryStep,
    };

    return {
        complete: steps.slice(0, currentIndex).map((x) => completeStepMap[x]),
        current: { date, text },
        toDo: steps.slice(currentIndex + 1).map((x) => toDoStepMap[x]),
    };
};

const TripSummaryMobile = localize((props) => {
    const {
        'data-id': dataId,
        t,
        tripId,
        url,
    } = props;

    const onClick = () => {
        events.changeTripButtonClicked({
            tripId,
            tripSummary: true,
        });
    };

    return (
        <div className={styles.returnButtonContainer} data-id={dataId}>
            <A
                className={styles.closeButton}
                href={url}
                onClick={onClick}
                role="button"
            >
                <img alt="back" src={arrow} />
            </A>
            <span className={styles.text}>
                {t('trip-summary')}
            </span>
        </div>
    );
}, 'baseApp');

function SearchBanner(props) {
    const {
        bannerText = '',
        changeDepartTripUrl = '/',
        changeReturnTripUrl,
        className = '',
        'data-id': dataId = '',
        departDate = '',
        destinationCityName = '',
        i18nMoment,
        locale,
        originCityName = '',
        outboundTripId = '',
        returnDate = '',
        returnTripId = '',
        step = DEPART_DIRECTION,
        t,
    } = props;
    const hasReturn = !!returnDate;
    const departDateTimestamp = i18nMoment(departDate).shortDayAndWeekday(locale);
    const returnDateTimestamp = i18nMoment(returnDate).shortDayAndWeekday(locale);

    const getBannerText = () => {
        if (step === DEPART_DIRECTION) {
            return t('departure-to', {
                city: destinationCityName,
            });
        }

        if (step === RETURN_DIRECTION) {
            return t('return-to', {
                city: originCityName,
            });
        }

        return bannerText;
    };

    const getBannerDate = () => {
        if (step === DEPART_DIRECTION) {
            return t('on_searchBanner', { date: departDateTimestamp });
        }

        if (step === RETURN_DIRECTION) {
            return t('on_searchBanner', { date: returnDateTimestamp });
        }

        return undefined;
    };

    return (
        <Fragment>
            {step === TRIP_SUMMARY && (
                <TripSummaryMobile
                    data-id={dataId}
                    tripId={hasReturn
                        ? returnTripId
                        : outboundTripId}
                    url={hasReturn
                        ? changeReturnTripUrl
                        : changeDepartTripUrl}
                />
            )}
            <div className={classNames(
                className,
                styles.searchBanner,
                {
                    [styles.searchContent]: step !== TRIP_SUMMARY,
                },
            )} data-id={dataId}>
                <Layout className={styles.columnContainer}>
                    <BannerCardList
                        searchBannerState={
                            getSearchBannerState(
                                step,
                                getBannerText(),
                                getBannerDate(),
                                t,
                                hasReturn,
                                changeDepartTripUrl,
                                outboundTripId,
                                changeReturnTripUrl,
                                returnTripId,
                                departDateTimestamp,
                                returnDateTimestamp,
                            )
                        }
                    />
                </Layout>
            </div>
        </Fragment>
    );
}

SearchBanner.propTypes = propTypes;

export {
    CHECKOUT,
    SearchBanner,
    getSearchBannerState,
};
export default localize(SearchBanner, ['i18nMoment', 't'], 'baseApp');
